/**
 * Écran de connexion au back-office (DECISIONS §4.2 : `POST /admin/auth/login`).
 *
 * Trois refus distincts, parce qu'ils appellent trois réactions différentes :
 * - `403 FORBIDDEN` : le compte existe mais n'est pas administrateur — message explicite, aucun
 *   jeton n'est délivré (l'API ne crée même pas de session dans ce cas) ;
 * - `401 AUTH_INVALID_CREDENTIALS` : adresse ou mot de passe faux ;
 * - réseau : l'API ne répond pas, la saisie est conservée et le bouton propose de réessayer.
 *
 * En cas de refus, le store reste vide : la garde de route ne laisse donc entrer nulle part.
 */
import { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';

import { connexionAdmin } from '../api/admin';
import { versErreurApi } from '../api/erreurs';
import { M } from '../catalogue/messages';
import { useSessionAdmin } from '../session/session-admin';
import { BandeauErreur } from '../ui/Bandeau';
import { Bouton } from '../ui/Bouton';
import { Champ } from '../ui/Champ';

import type { ErreurApi } from '../api/erreurs';
import type { FormEvent } from 'react';

/** État de navigation transportant l'écran demandé avant la redirection par la garde. */
interface EtatRedirection {
  readonly depuis?: string;
}

export function EcranConnexion() {
  const jeton = useSessionAdmin((etat) => etat.jeton);
  const definirSession = useSessionAdmin((etat) => etat.definirSession);
  const navigation = useNavigate();
  const emplacement = useLocation();

  const [email, setEmail] = useState('');
  const [motDePasse, setMotDePasse] = useState('');
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState<ErreurApi | null>(null);
  const [refus, setRefus] = useState<string | null>(null);

  const destination = (emplacement.state as EtatRedirection | null)?.depuis ?? '/cartes';

  if (jeton !== null) return <Navigate to={destination} replace />;

  async function soumettre(evenement: FormEvent<HTMLFormElement>): Promise<void> {
    evenement.preventDefault();
    if (enCours) return;
    setErreur(null);
    setRefus(null);
    if (email.trim().length === 0 || motDePasse.length === 0) {
      setRefus(M.connexion.champsRequis);
      return;
    }
    setEnCours(true);
    try {
      const resultat = await connexionAdmin(email.trim(), motDePasse);
      definirSession({
        jeton: resultat.accessToken,
        admin: { id: resultat.admin.id, email: resultat.admin.email },
      });
      navigation(destination, { replace: true });
    } catch (inconnu) {
      const erreurApi = versErreurApi(inconnu);
      if (erreurApi.code === 'FORBIDDEN') {
        setRefus(M.connexion.refusNonAdmin);
      } else if (erreurApi.code === 'AUTH_INVALID_CREDENTIALS') {
        setRefus(M.connexion.identifiantsInvalides);
      } else {
        setErreur(erreurApi);
      }
    } finally {
      setEnCours(false);
    }
  }

  return (
    <div className="ecran-connexion">
      <form className="carte-connexion" onSubmit={(evenement) => void soumettre(evenement)}>
        <h1 className="carte-connexion__titre">{M.connexion.titre}</h1>
        <p className="carte-connexion__sous-titre">{M.connexion.sousTitre}</p>

        {refus === null ? null : (
          <p className="bandeau bandeau--erreur" role="alert">
            {refus}
          </p>
        )}
        {erreur === null ? null : <BandeauErreur erreur={erreur} />}

        <Champ
          id="connexion-email"
          etiquette={M.connexion.email}
          type="email"
          autoComplete="username"
          value={email}
          onChange={(evenement) => setEmail(evenement.target.value)}
        />
        <Champ
          id="connexion-mot-de-passe"
          etiquette={M.connexion.motDePasse}
          type="password"
          autoComplete="current-password"
          value={motDePasse}
          onChange={(evenement) => setMotDePasse(evenement.target.value)}
        />
        <Bouton type="submit" enCours={enCours} texteEnCours={M.connexion.validationEnCours}>
          {M.connexion.valider}
        </Bouton>
      </form>
    </div>
  );
}
